import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { LogManager } from '../../utility/Logger';
import PropertyDropdown from '../common/PropertyDropdown';
import useLayoutAwareFitPropertyState, {
  isInnerVideoElement,
  LAYOUT_TYPES,
  NON_LAYOUT_TYPES,
} from './useLayoutAwareFitPropertyState';

import type { ElementState } from '../../renderer/ElementState';

interface Props {
  activeElement: ElementState;
  propertyName: string;
  defaultValue: any;
  options?: Array<{ caption: string; value: any }>;
}

const logger = LogManager.getLogger('VideoFitPropertySelect');

export const VideoFitPropertySelect: React.FC<Props> = observer(props => {
  const videoCreator = useVideoCreatorStore();

  const options = props.options || [
    { caption: 'Cover', value: 'cover' },
    { caption: 'Contain', value: 'contain' },
  ];

  const {
    hasMixedOptions,
    updateFitProperty,
    determineValueForStackedLayout,
    handleStackedLayoutFitChange,
  } = useLayoutAwareFitPropertyState({
    options,
    propertyName: props.propertyName,
    defaultValue: props.defaultValue,
    activeElement: props.activeElement,
  });

  // First get default value from element properties
  let value =
    props.activeElement.source[props.propertyName] ?? props.defaultValue;
  let elementId = props.activeElement.source.id;

  // Check if we're dealing with the inner video element
  const isInnerVideo = isInnerVideoElement(
    elementId,
    props.activeElement.source.type,
    videoCreator.rootStore.videoSourceStore.source,
  );

  // For inner video elements, just use the direct fit property
  if (isInnerVideo && !hasMixedOptions) {
    logger.info('Using inner video path');
    // Make sure the value is valid for a video element
    if (!NON_LAYOUT_TYPES.includes(value)) {
      value = props.defaultValue;
    }
    return (
      <PropertyDropdown
        value={value}
        onChange={async newValue => {
          logger.info('Inner video onChange called', newValue);
          // Just update the fit property directly
          await updateFitProperty(newValue, elementId);
        }}
        defaultValue={value}
        values={options.map(({ value: optionValue, caption }) => ({
          value: optionValue,
          caption,
          selected: optionValue === value,
        }))}
      />
    );
  }

  logger.info('Using stacked layout path', {
    isInnerVideo,
    hasMixedOptions,
  });
  value = determineValueForStackedLayout(elementId, value);

  return (
    <PropertyDropdown
      value={value}
      onChange={async newValue => {
        logger.info('Stacked layout onChange called', newValue);
        await handleStackedLayoutFitChange(newValue);
      }}
      defaultValue={value}
      values={options.map(({ value: optionValue, caption }) => ({
        value: optionValue,
        caption,
        // Force dropdown to show correct selection for all non-layout values
        selected: !LAYOUT_TYPES.includes(optionValue) && optionValue === value,
      }))}
    />
  );
});
